import { z } from "zod";

// Post-Clerk signup finalization. Clerk owns email/password verification;
// this schema validates the profile fields we mirror into our own users table.
// The account comes from the request's Clerk session, never from this input.
export const UserSignupSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required.")
    .max(50, "First name must be 50 characters or less."),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required.")
    .max(50, "Last name must be 50 characters or less."),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Email is required.")
    .email("Invalid email address."),
  phoneNumber: z
    .string()
    .trim()
    .min(10, "Phone number must be at least 10 digits.")
    .max(15, "Phone number must be at most 15 digits.")
    .optional(),
});

export type UserSignupForm = z.infer<typeof UserSignupSchema>;

export const UserProfileSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required.")
    .max(50, "First name must be 50 characters or less."),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required.")
    .max(50, "Last name must be 50 characters or less."),
  email: z
    .string()
    .trim()
    .min(1, "Email is required.")
    .email("Invalid email address."),
  photo: z.string().optional(), // Handling as optional, can be a File object
  phoneNumber: z
    .string()
    .trim()
    .optional()
    .transform((val) => (val === "" ? undefined : val))
    .refine((val) => !val || (val.length >= 10 && val.length <= 15), {
      message: "Phone number must be between 10 and 15 digits.",
    }),
  title: z
    .string()
    .trim()
    .max(100, "Title must be 100 characters or less.")
    .optional(),
  bio: z
    .string()
    .trim()
    .max(500, "Description must be 500 characters or less.")
    .optional(),
  clerkUserId: z.string().trim().optional(),
  removePhoto: z.boolean().optional(),
});

export type UserProfileForm = z.infer<typeof UserProfileSchema>;

/** How each profile field is labelled to the user, mid-sentence. */
const PROFILE_FIELD_LABELS: Record<string, string> = {
  firstName: "first name",
  lastName: "last name",
  email: "email",
  phoneNumber: "phone number",
  title: "title",
  bio: "description",
};

/**
 * Labels the profile fields blocking a save, for actions that submit the whole
 * profile as a side effect (the avatar upload does). Those actions can only
 * name the fields to fix if they ask which ones failed — hardcoding "name and
 * email" sends the user to fields that are already fine when it was really the
 * description that ran long.
 *
 * Returns an empty array when the values are valid, or when only fields with
 * no visible label failed.
 */
export function invalidProfileFieldLabels(values: unknown): string[] {
  const result = UserProfileSchema.safeParse(values);
  if (result.success) return [];

  const labels = result.error.issues.map(
    (issue) => PROFILE_FIELD_LABELS[String(issue.path[0])],
  );
  return [...new Set(labels.filter((label) => label !== undefined))];
}

export const UserSigninSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required.")
    .email("Invalid email address."),
  password: z
    .string()
    .min(1, "Please enter your password.")
    .min(8, "Your password must have 8 characters or more."),
});

export const codeSchema = z.object({
  code: z
    .array(z.string().min(1, "Each field must have a value"))
    .length(6, "Code must be 6 digits")
    .refine(
      (input) => input.every((digit) => /^\d$/.test(digit)),
      "All fields must be digits",
    ),
});
export type UserSigninForm = z.infer<typeof UserSigninSchema>;

export const UserForgotPasswordSchema = z
  .object({
    email: z
      .string()
      .trim()
      .min(1, "Email is required.")
      .email("Invalid email address."),
    password: z
      .string()
      .min(1, "Please enter your password.")
      .min(8, "Your password must have 8 characters or more."),
    password2: z.string().min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.password2, {
    message: "The two passwords do not match.",
    path: ["password2"],
  });

export type UserForgotPasswordForm = z.infer<typeof UserForgotPasswordSchema>;
